import React, { useState, useEffect, useRef } from "react";

import * as Sentry from "@sentry/react";
import { HiPlusCircle } from "react-icons/hi";
import { handleValidatePhoneNumber } from "../../../Utilities/general";
import InstructionsBottomSheet from "../../../components/Modals/InstructionsBottomSheet";


/**
 * Build FormData objects for files separated by section.
 * Returns: { pickup: FormData|null, dropoff: FormData|null, stops: Array<FormData|null> }
 * Each FormData contains the file binary(s) uploaded for that section.
 * Files without a `file` property (i.e. already-serialized metadata) are skipped.
 */
export function buildFilesFormData({ pickupFiles = [], dropoffFiles = [], stopsFiles = [] } = {}) {
  const result = { pickup: null, dropoff: null, stops: [] };

  const makeForm = (files, formKey) => {
    const fd = new FormData();
    let appended = false;
    (files || []).forEach((f, i) => {
      // Only append if we have a real File object (from input/camera)
      if (f && f.file instanceof File) {
        // key naming: use section-specific array keys so backend can map easily
        fd.append(formKey, f.file, f.name || `file_${i}`);
        appended = true;
      }
    });
    return appended ? fd : null;
  };

  result.pickup = makeForm(pickupFiles, "pickup_files[]");
  result.dropoff = makeForm(dropoffFiles, "dropoff_files[]");

  // stopsFiles is expected to be an array of arrays: stopsFiles[index] = [fileObj, ...]
  (stopsFiles || []).forEach((filesArr, idx) => {
    // use a per-stop key so backend can accept separate payloads or you can upload individually
    const fd = makeForm(filesArr, `stop_${idx}_files[]`);
    result.stops.push(fd);
  });

  return result;
}

const ContactInfoScreen = ({

  //Instructions Text Set And Use State (match parent naming)
  pickUpInstructions,
  setPickUpInstructions,
  dropOffInstructions,
  setDropOffInstructions,

  pickUpPersonName,
  pickUpPersonPhone,
  stops,
  dropoffLocation,
  dropOffPersonName,
  dropOffPersonPhone,
  // bookingFiles = [],
  handleUpdateBookingObject,
  errorObject,
  setErrorObject,
}) => {
  // State for checkboxes
  const [useMyInfo, setUseMyInfo] = useState(false);
  const [sameAsPickup, setSameAsPickup] = useState(false);

  // State for instructions bottom sheet
  const [isInstructionsSheetOpen, setIsInstructionsSheetOpen] = useState(false);
  const [currentLocationType, setCurrentLocationType] = useState(null);
  const [currentLocationIndex, setCurrentLocationIndex] = useState(null);

  // Refs for headings and container to draw connector line
  const containerRef = useRef(null);
  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);

  // State for instructions and files for each location

  const [pickupFiles, setPickupFiles] = useState([]);
  const [dropoffFiles, setDropoffFiles] = useState([]);
  const [stopsFiles, setStopsFiles] = useState([]);

  // keep a ref to the latest stopsFiles so we can preserve real File objects
  // when the `stops` prop contains only serialized metadata (no `file`)
  const stopsFilesRef = useRef([]);
  useEffect(() => {
    stopsFilesRef.current = stopsFiles;
  }, [stopsFiles]);


  const [stopsInstructions, setStopsInstructions] = useState([]);
  const [stopsSameAsPickup, setStopsSameAsPickup] = useState([]);



  // Initialize stops arrays when stops prop changes
  useEffect(() => {
    // Populate stopsInstructions and stopsFiles from the `stops` prop
    // so previously saved instructions/files are loaded (persisted in VBO).
    if (Array.isArray(stops) && stops.length > 0) {
      const instr = stops.map((s) => s?.instructions || "");

      // Build files array but prefer any existing File objects we already have
      const filesArr = (stops || []).map((s, idx) => {
        const sfiles = Array.isArray(s?.files) ? s.files : [];

        // If server/prop already provided real File objects, keep them
        if (sfiles.some((f) => f && f.file instanceof File)) return sfiles;

        // Otherwise, if we previously had File objects in local state for this stop, preserve them
        const prev = stopsFilesRef.current && stopsFilesRef.current[idx];
        if (Array.isArray(prev) && prev.some((f) => f && f.file instanceof File)) return prev;

        // Fall back to whatever was provided (likely serialized metadata)
        return sfiles;
      });

      setStopsInstructions(instr);
      setStopsFiles(filesArr);
    } else {
      setStopsInstructions([]);
      setStopsFiles([]);
    }
  }, [stops]);

  // Initialize per-stop "Same as pick-up" flags to false for each stop
  useEffect(() => {
    // Preserve existing flags when possible to avoid flicker when parent updates stops
    setStopsSameAsPickup((prev) => {
      if (!Array.isArray(stops) || stops.length === 0) return [];
      if (Array.isArray(prev) && prev.length === stops.length) return prev;
      const newArr = Array(stops.length).fill(false);
      if (Array.isArray(prev) && prev.length > 0) {
        for (let i = 0; i < Math.min(prev.length, stops.length); i++) {
          newArr[i] = !!prev[i];
        }
      }
      return newArr;
    });
  }, [stops]);

  // State to control the single connecting line between Pick-up and Drop-off headings
  const [lineStyle, setLineStyle] = useState({
    visible: false,
    top: 0,
    height: 0,
    left: 0,
  });

  // Compute and set the line position & size between the two heading elements
  useEffect(() => {
    function computeLine() {
      if (!dropoffLocation) {
        setLineStyle({ visible: false, top: 0, height: 0, left: 0 });
        return;
      }

      const c = containerRef.current;
      const p = pickupRef.current;
      const d = dropoffRef.current;
      if (!c || !p || !d) return;

      const cRect = c.getBoundingClientRect();
      const pRect = p.getBoundingClientRect();
      const dRect = d.getBoundingClientRect();

      // Use center Y of each heading so the line connects visually from heading to heading
      const startY = pRect.top + pRect.height / 2 - cRect.top;
      const endY = dRect.top + dRect.height / 2 - cRect.top;
      const top = Math.min(startY, endY);
      const height = Math.max(0, Math.abs(endY - startY));

      // Position line slightly to the left of the heading text for visual separation
      const left = pRect.left - cRect.left - 18;

      setLineStyle({ visible: height > 0, top, height, left });
    }

    computeLine();
    const onResize = () => computeLine();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [
    dropoffLocation,
    stops,
    pickUpInstructions,
    dropOffInstructions,
    stopsInstructions,
  ]);

  // Get user profile from localStorage
  const getUserProfile = () => {
    try {
      const userProfile = localStorage.getItem("wemoovezUserProfile");
      return userProfile ? JSON.parse(userProfile) : null;
    } catch (error) {
      console.error("Error parsing user profile:", error);
      return null;
    }
  };

  // Handle "Use my info" checkbox change
  const handleUseMyInfoChange = (e) => {
    const isChecked = e.target.checked;
    setUseMyInfo(isChecked);

    if (isChecked) {
      const userProfile = getUserProfile();
      if (userProfile) {
        const fullName = `${userProfile.firstName || ""} ${userProfile.lastName || ""
          }`.trim();
        const phone = userProfile.phone || "";

        // Update pick-up fields
        handleUpdateBookingObject("pickUpPersonName", fullName, "18.1");
        handleUpdateBookingObject("pickUpPersonPhone", phone, "18.2");

        // Clear any errors
        setErrorObject({
          ...errorObject,
          pickUpPersonName: false,
          pickUpPersonPhone: false,
        });

        // Also immediately persist to VBO in localStorage to avoid race conditions
        try {
          const oldVBO = JSON.parse(localStorage.getItem("VBO")) || {};
          const merged = {
            ...oldVBO,
            pickUpPersonName: fullName,
            pickUpPersonPhone: phone,
            updatedAt: new Date().toISOString(),
          };
          localStorage.setItem("VBO", JSON.stringify(merged));
        } catch (err) {
          console.error("Failed to write VBO on UseMyInfo:", err);
        }
      }
    } else {
      // Clear fields when unchecked
      handleUpdateBookingObject("pickUpPersonName", "", "18.1");
      handleUpdateBookingObject("pickUpPersonPhone", "", "18.2");
      try {
        const oldVBO = JSON.parse(localStorage.getItem("VBO")) || {};
        const merged = {
          ...oldVBO,
          pickUpPersonName: "",
          pickUpPersonPhone: "",
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem("VBO", JSON.stringify(merged));
      } catch (err) {
        console.error("Failed to clear VBO on UseMyInfo uncheck:", err);
      }
    }
  };

  // Handle "Same as pick-up" checkbox change
  const handleSameAsPickupChange = (e) => {
    const isChecked = e.target.checked;
    setSameAsPickup(isChecked);

    if (isChecked) {
      // Copy pick-up values to drop-off
      handleUpdateBookingObject("dropOffPersonName", pickUpPersonName, "18.7");
      handleUpdateBookingObject(
        "dropOffPersonPhone",
        pickUpPersonPhone,
        "18.8"
      );

      // Clear any errors
      setErrorObject({
        ...errorObject,
        dropOffPersonName: false,
        dropOffPersonPhone: false,
      });

      // Persist drop-off values to VBO immediately
      try {
        const oldVBO = JSON.parse(localStorage.getItem("VBO")) || {};
        const merged = {
          ...oldVBO,
          dropOffPersonName: pickUpPersonName || "",
          dropOffPersonPhone: pickUpPersonPhone || "",
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem("VBO", JSON.stringify(merged));
      } catch (err) {
        console.error("Failed to write VBO on SameAsPickup:", err);
      }
    } else {
      // Clear drop-off fields when unchecked
      handleUpdateBookingObject("dropOffPersonName", "", "18.7");
      handleUpdateBookingObject("dropOffPersonPhone", "", "18.8");
      try {
        const oldVBO = JSON.parse(localStorage.getItem("VBO")) || {};
        const merged = {
          ...oldVBO,
          dropOffPersonName: "",
          dropOffPersonPhone: "",
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem("VBO", JSON.stringify(merged));
      } catch (err) {
        console.error("Failed to clear VBO on SameAsPickup uncheck:", err);
      }
    }
  };

  // Handle manual field changes and auto-uncheck "Use my info"
  const handlePickUpNameChange = (e) => {
    setErrorObject({ ...errorObject, pickUpPersonName: false });
    const newValue = handleReturnText(e);
    handleUpdateBookingObject("pickUpPersonName", newValue, "18.1");

    // Auto-uncheck "Use my info" if field is manually edited
    if (useMyInfo) {
      setUseMyInfo(false);
    }

    // Auto-uncheck "Same as pick-up" if pickup info changes
    if (sameAsPickup) {
      setSameAsPickup(false);
    }
    // Clear any per-stop same-as flags when pickup changes
    if (stopsSameAsPickup && stopsSameAsPickup.some(Boolean)) {
      setStopsSameAsPickup(Array(stopsSameAsPickup.length).fill(false));
    }
  };

  const handlePickUpPhoneChange = (e) => {
    setErrorObject({ ...errorObject, pickUpPersonPhone: false });
    handleValidatePhoneNumber(e, (num) => {
      handleUpdateBookingObject("pickUpPersonPhone", num, "18.2");

      // Auto-uncheck "Use my info" if field is manually edited
      if (useMyInfo) {
        setUseMyInfo(false);
      }

      // Auto-uncheck "Same as pick-up" if pickup info changes
      if (sameAsPickup) {
        setSameAsPickup(false);
      }
      // Clear any per-stop same-as flags when pickup changes
      if (stopsSameAsPickup && stopsSameAsPickup.some(Boolean)) {
        setStopsSameAsPickup(Array(stopsSameAsPickup.length).fill(false));
      }
    });
  };

  // Handle per-stop "Same as pick-up" checkbox
  const handleStopSameAsPickupChange = (index, e) => {
    const isChecked = e.target.checked;
    try {
      const newFlags = [...(stopsSameAsPickup || [])];
      newFlags[index] = isChecked;
      setStopsSameAsPickup(newFlags);

      const updatedStops = (stops || []).map((s, i) =>
        i === index
          ? {
            ...s,
            personName: isChecked ? pickUpPersonName : "",
            personPhone: isChecked ? pickUpPersonPhone : "",
          }
          : s
      );

      handleUpdateBookingObject("stops", updatedStops, "18.5");

      // Also persist updated stops to VBO immediately to ensure consistency
      try {
        const oldVBO = JSON.parse(localStorage.getItem("VBO")) || {};
        const merged = {
          ...oldVBO,
          stops: updatedStops,
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem("VBO", JSON.stringify(merged));
      } catch (err) {
        console.error("Failed to write VBO on Stop SameAsPickup:", err);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  };

  // Handle drop-off field changes and auto-uncheck "Same as pick-up"
  const handleDropOffNameChange = (e) => {
    setErrorObject({ ...errorObject, dropOffPersonName: false });
    const newValue = handleReturnText(e);
    handleUpdateBookingObject("dropOffPersonName", newValue, "18.7");

    // Auto-uncheck "Same as pick-up" if field is manually edited
    if (sameAsPickup) {
      setSameAsPickup(false);
    }
  };

  const handleDropOffPhoneChange = (e) => {
    setErrorObject({ ...errorObject, dropOffPersonPhone: false });
    handleValidatePhoneNumber(e, (num) => {
      handleUpdateBookingObject("dropOffPersonPhone", num, "18.8");

      // Auto-uncheck "Same as pick-up" if field is manually edited
      if (sameAsPickup) {
        setSameAsPickup(false);
      }
    });
  };

  // alphabets
  const alphabets = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
  ];

  const handleReturnText = (e) => {
    let text = e?.target?.value;
    if (!text) return "";
    let textArr = /[a-zA-Z][a-zA-Z ]*/.exec(text);
    return textArr?.length ? textArr.join("") : "";
  };

  // Handle opening instructions bottom sheet for different locations
  const openInstructionsSheet = (locationType, locationIndex = null) => {
    setCurrentLocationType(locationType);
    setCurrentLocationIndex(locationIndex);
    setIsInstructionsSheetOpen(true);
  };

  // Handle closing instructions bottom sheet
  const closeInstructionsSheet = () => {
    setIsInstructionsSheetOpen(false);
    setCurrentLocationType(null);
    setCurrentLocationIndex(null);
  };

  // Handle saving instructions and files
  const handleSaveInstructions = (
    instructions,
    files,
    locationType,
    locationIndex
  ) => {
    switch (locationType) {
      case "pickup":
        setPickUpInstructions(instructions);
        setPickupFiles(files);
        try {
          // serialize files (don't store File objects in localStorage)
          const serialized = (files || []).map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type,
            section: "pickup",
            sectionIndex: null,
            timestamp: new Date().toISOString(),
          }));

          // merge with existing bookingFiles by removing previous pickup entries
          // const merged = (bookingFiles || []).filter(
          //   (bf) => bf.section !== "pickup"
          // );

          const updatedBookingFiles = [...serialized];

          // Persist instructions and bookingFiles to booking (VBO)
          handleUpdateBookingObject("pickUpInstructions", instructions, "instructions_save");
          // handleUpdateBookingObject("bookingFiles", updatedBookingFiles, "instructions_save");
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
        break;
      case "dropoff":
        setDropOffInstructions(instructions);
        setDropoffFiles(files);
        try {
          const serialized = (files || []).map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type,
            section: "dropoff",
            sectionIndex: null,
            timestamp: new Date().toISOString(),
          }));

          // const merged = (bookingFiles || []).filter(
          //   (bf) => bf.section !== "dropoff"
          // );
          const updatedBookingFiles = [...serialized];

          handleUpdateBookingObject("dropOffInstructions", instructions, "instructions_save");
          // handleUpdateBookingObject("bookingFiles", updatedBookingFiles, "instructions_save");
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
        break;
      case "stop":
        const newStopsInstructions = [...stopsInstructions];
        const newStopsFiles = [...stopsFiles];
        newStopsInstructions[locationIndex] = instructions;
        newStopsFiles[locationIndex] = files;
        setStopsInstructions(newStopsInstructions);
        setStopsFiles(newStopsFiles);
        try {
          // update stops array in booking with instructions and serialized files
          const serializedFiles = (files || []).map((f) => ({
            id: f.id,
            name: f.name,
            type: f.type,
            section: "stop",
            sectionIndex: locationIndex,
            timestamp: new Date().toISOString(),
          }));

          const updatedStops = (stops || []).map((s, i) => {
            if (i === locationIndex) {
              return {
                ...s,
                instructions: instructions,
                files: serializedFiles,
              };
            }
            return s;
          });

          handleUpdateBookingObject("stops", updatedStops, "instructions_save");

          // also merge serialized stop files into bookingFiles array
          // const mergedOther = (bookingFiles || []).filter(
          //   (bf) => !(bf.section === "stop" && bf.sectionIndex === locationIndex)
          // );

          const updatedBookingFiles = [...serializedFiles];
          handleUpdateBookingObject("bookingFiles", updatedBookingFiles, "instructions_save");
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative px-[33px] font-instrument w-full max-w-container"
    >
      {/* local placeholder style to enforce placeholder color/size */}
      <style>{`.custom-placeholder::placeholder{ color: #373559 !important; font-size:14px !important; opacity:1; }`}</style>
      {lineStyle.visible && (
        <>
          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left}px`,
              top: `${lineStyle.top}px`,
              height: `${lineStyle.height}px`,
              width: "2px",
              backgroundColor: "#211F59",
              borderRadius: "1px",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          {/* Top square (10x10) centered on the line's top endpoint */}
          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left - 4}px`,
              top: `${lineStyle.top - 5}px`,
              width: "10px",
              height: "10px",
              backgroundColor: "#211F59",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />

          {/* Bottom square (10x10) centered on the line's bottom endpoint */}
          <div
            style={{
              position: "absolute",
              left: `${lineStyle.left - 4}px`,
              top: `${lineStyle.top + lineStyle.height - 5}px`,
              width: "10px",
              height: "10px",
              backgroundColor: "#211F59",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        </>
      )}

      <div className="text-[24px] text-[#211F59] font-semibold mb-[27px]" style={{ lineHeight: "23px" }}>
        Contact info
      </div>

      <div className="flex flex-col w-full  pl-[26px]">
        {/* Pick-up Section */}
        <div className="w-full relative">
          <div
            ref={pickupRef}
            className="text-[#211F59] text-[16px] font-semibold mb-[8px]"
          >
            Pick-up
          </div>
          <div className="text-[#211F59] text-[11px] font-normal mb-2">
            Contact person (if different from the person booking).
          </div>

          {/* Use my info checkbox */}
          <div className="flex items-center mb-[11.46px] gap-2">
            <input
              type="checkbox"
              id="useMyInfo"
              checked={useMyInfo}
              onChange={handleUseMyInfoChange}
              className="w-[18.70px] h-[18.70px] cursor-pointer accent-[#211F59] rounded-none"
            />
            <label
              htmlFor="useMyInfo"
              className="text-[#211F59] font-semibold text-[12px] select-none"
            >
              Use my info
            </label>
          </div>

          <div
            className="w-full flex items-center"
            style={{ marginTop: 0, marginBottom: "8.45px" }}
          >
            <input
              className={
                "w-full  bg-[#fff5f5] px-[13.55px] py-[15px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
              }
              placeholder="First & last name"
              value={pickUpPersonName}
              onChange={handlePickUpNameChange}
              style={{
                height: "48px",
                backgroundColor: "#E6E6E6",
                fontWeight: 400,
                fontSize: "14px",
              }}
            />
          </div>
          <div className="w-full flex items-center mb-[13px]">
            <input
              value={pickUpPersonPhone}
              className={
                "w-full  bg-[#fff5f5] px-[13.55px] py-[15px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
              }
              placeholder="Phone number"
              onChange={handlePickUpPhoneChange}
              style={{
                height: "48px",
                backgroundColor: "#E6E6E6",
                fontWeight: 400,
                fontSize: "14px",
              }}
            />
          </div>

          {/* Additional instructions or files with + button */}
          <div
            className="flex items-center justify-start gap-x-2 mb-[28.32px]"
            onClick={() => openInstructionsSheet("pickup")}
          >
            <span className="text-[#211F59] text-[12px] font-semibold select-none cursor-pointer">
              Additional instructions (optional)
              {(pickUpInstructions || pickupFiles.length > 0) && (
                <span className="text-[var(--primary)] ml-2">• Added</span>
              )}
            </span>
            <img
              src="/Vision Agent Icons/Add Icon/Addiconpurple.svg"
              alt="add icon"
              width={16.74}
            />
          </div>
        </div>

        {/* Stops Section */}
        {stops?.map((stopData, index) => (
          <div className="w-full relative mb-[28.32px]" key={`stop-${index}`}>
            <div className="text-[#211F59] text-[16px] font-semibold mb-[8px]">
              Pick-up {index + 2}
            </div>
            <div className="text-[#211F59] text-[11px] font-normal mb-2">
              Contact person (if different from the person booking).
            </div>

            <div className="flex items-center gap-2 mb-[11.46px]">
              <input
                type="checkbox"
                id={`sameAsPickup-stop-${index}`}
                checked={!!stopsSameAsPickup[index]}
                onChange={(e) => handleStopSameAsPickupChange(index, e)}
                className="w-[18.70px] h-[18.70px] cursor-pointer accent-[#211F59] rounded-none"
              />
              <label
                htmlFor={`sameAsPickup-stop-${index}`}
                className="text-[#211F59] font-semibold text-[12px] select-none"
              >
                Same as pick-up
              </label>
            </div>

            <div className="w-full flex items-center mb-[8px]">
              <input
                className="w-full  bg-[#fff5f5] px-[13.55px] py-[15px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
                placeholder="First & last name (optional)"
                value={stops[index]?.personName}
                onChange={(e) => {
                  try {
                    const stopInfo = [...(stops || [])];
                    stopInfo[index] = { ...stopInfo[index], personName: handleReturnText(e) };
                    handleUpdateBookingObject("stops", stopInfo, "18.4");

                    // Auto-uncheck this stop's "Same as pick-up" if stops info changes
                    if (stopsSameAsPickup[index]) {
                      const newArr = [...stopsSameAsPickup];
                      newArr[index] = false;
                      setStopsSameAsPickup(newArr);
                    }
                  } catch (error) {
                    Sentry.captureException(error);
                    console.log(error);
                  }
                }}
                style={{
                  height: "48px",
                  backgroundColor: "#E6E6E6",
                  fontWeight: 400,
                  fontSize: "14px",
                }}
              />
            </div>
            <div
              className="w-full flex items-center"
              style={{ marginTop: 0, marginBottom: "8.45px" }}
            >
              <input
                className="w-full  bg-[#fff5f5]  px-[13.55px] py-[15px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
                placeholder="Phone number (optional)"
                value={stops[index]?.personPhone}
                onChange={(e) =>
                  handleValidatePhoneNumber(e, (num) => {
                    const stopInfo = [...(stops || [])];
                    stopInfo[index] = { ...stopInfo[index], personPhone: num };
                    handleUpdateBookingObject("stops", stopInfo, "18.5");

                    // Auto-uncheck this stop's "Same as pick-up" if stops info changes
                    if (stopsSameAsPickup[index]) {
                      const newArr = [...stopsSameAsPickup];
                      newArr[index] = false;
                      setStopsSameAsPickup(newArr);
                    }
                  })
                }
                style={{
                  height: "48px",
                  backgroundColor: "#E6E6E6",
                  fontWeight: 400,
                  fontSize: "14px",
                }}
              />
            </div>

            {/* Additional instructions or files with + button */}
            <div
              className="flex items-center justify-start gap-x-2"
              onClick={() => openInstructionsSheet("stop", index)}
            >
              <span className="text-[#211F59] text-[12px] font-semibold select-none cursor-pointer mt-2">
                Additional instructions (optional)
                {(stopsInstructions[index] ||
                  (stopsFiles[index] && stopsFiles[index].length > 0)) && (
                    <span className="text-[var(--primary)] ml-2">• Added</span>
                  )}
              </span>
              <img
                src="/Vision Agent Icons/Add Icon/Addiconpurple.svg"
                alt="add icon"
                width={16.74}
                className="mt-2"
              />
            </div>
          </div>
        ))}

        {/* Drop-off Section */}
        {dropoffLocation && (
          <div className="w-full relative mb-2">
            <div
              ref={dropoffRef}
              className="text-[16px] text-[#211F59] font-semibold mb-2"
            >
              Drop-off
            </div>
            <div className="text-[#211F59] text-[11px] mb-[9.45px]">
              Contact person (if different from the pick-up).
            </div>

            {/* Same as pick-up checkbox */}
            <div className="flex items-center gap-2 mb-[12.45px]">
              <input
                type="checkbox"
                id="sameAsPickup"
                checked={sameAsPickup}
                onChange={handleSameAsPickupChange}
                className="w-[18.70px] h-[18.70px] cursor-pointer accent-[#211F59] rounded-none"
              />
              <label
                htmlFor="sameAsPickup"
                className="text-[12px] font-semibold select-none text-[#211F59]"
              >
                Same as pick-up
              </label>
            </div>

            <div
              className="w-full flex items-center"
              style={{ marginTop: 0, marginBottom: "8.45px" }}
            >
              <input
                className="w-full  bg-[#fff5f5] px-[13.55px] py-[15px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
                placeholder="First & last name (optional)"
                value={dropOffPersonName}
                onChange={handleDropOffNameChange}
                style={{
                  height: "48px",
                  backgroundColor: "#E6E6E6",
                  fontWeight: 400,
                  fontSize: "14px",
                }}
              />
            </div>
            <div className="w-full flex items-center">
              <input
                value={dropOffPersonPhone}
                className={
                  "w-full  bg-[#fff5f5] px-[13.55px] py-[15px] mb-[13px] rounded-lg placeholder-[#373559] focus:border-[#7876A1] border-[1px] outline-none"
                }
                placeholder="Phone number (optional)"
                onChange={handleDropOffPhoneChange}
                style={{
                  height: "48px",
                  backgroundColor: "#E6E6E6",
                  fontWeight: 400,
                  fontSize: "14px",
                }}
              />
            </div>

            {/* Additional instructions or files with + button */}
            <div
              className="flex items-center justify-start gap-x-2 mb-[28.32px]"
              onClick={() => openInstructionsSheet("dropoff")}
            >
              <span className="text-[#211F59] text-[12px] font-semibold select-none cursor-pointer">
                Additional instructions (optional)
                {(dropOffInstructions || dropoffFiles.length > 0) && (
                  <span className="text-[var(--primary)] ml-2">• Added</span>
                )}
              </span>
              <img
                src="/Vision Agent Icons/Add Icon/Addiconpurple.svg"
                alt="add icon"
                width={16.74}
              />
            </div>
          </div>
        )}
      </div>

      {/* Instructions Bottom Sheet */}
      <InstructionsBottomSheet
        isOpen={isInstructionsSheetOpen}
        onClose={closeInstructionsSheet}
        locationType={currentLocationType}
        locationIndex={currentLocationIndex}
        instructions={
          currentLocationType === "pickup"
            ? pickUpInstructions
            : currentLocationType === "dropoff"
              ? dropOffInstructions
              : currentLocationType === "stop"
                ? stopsInstructions[currentLocationIndex] || ""
                : ""
        }
        files={
          currentLocationType === "pickup"
            ? pickupFiles
            : currentLocationType === "dropoff"
              ? dropoffFiles
              : currentLocationType === "stop"
                ? stopsFiles[currentLocationIndex] || []
                : []
        }
        onInstructionsChange={(instructions) => {
          try {
            if (currentLocationType === "pickup") {
              setPickUpInstructions(instructions);
              // persist immediately to VBO
              handleUpdateBookingObject("pickUpInstructions", instructions, "instructions_live");
            } else if (currentLocationType === "dropoff") {
              setDropOffInstructions(instructions);
              handleUpdateBookingObject("dropOffInstructions", instructions, "instructions_live");
            } else if (currentLocationType === "stop") {
              const newStopsInstructions = [...stopsInstructions];
              newStopsInstructions[currentLocationIndex] = instructions;
              setStopsInstructions(newStopsInstructions);

              // Persist stops array with updated instructions
              const updatedStops = (stops || []).map((s, i) =>
                i === currentLocationIndex ? { ...s, instructions } : s
              );
              handleUpdateBookingObject("stops", updatedStops, "instructions_live");
            }
          } catch (e) {
            Sentry.captureException(e);
            console.error(e);
          }
        }}
        onFilesChange={(files) => {
          try {
            if (currentLocationType === "pickup") {
              setPickupFiles(files);
              // serialize and merge into bookingFiles
              const serialized = (files || []).map((f) => ({
                id: f.id,
                name: f.name,
                type: f.type,
                section: "pickup",
                sectionIndex: null,
                timestamp: new Date().toISOString(),
              }));
              // const merged = (bookingFiles || []).filter((bf) => bf.section !== "pickup");
              // handleUpdateBookingObject("bookingFiles", [...serialized], "files_live");
            } else if (currentLocationType === "dropoff") {
              setDropoffFiles(files);
              const serialized = (files || []).map((f) => ({
                id: f.id,
                name: f.name,
                type: f.type,
                section: "dropoff",
                sectionIndex: null,
                timestamp: new Date().toISOString(),
              }));
              // const merged = (bookingFiles || []).filter((bf) => bf.section !== "dropoff");
              // handleUpdateBookingObject("bookingFiles", [...serialized], "files_live");
            } else if (currentLocationType === "stop") {
              const newStopsFiles = [...stopsFiles];
              newStopsFiles[currentLocationIndex] = files;
              setStopsFiles(newStopsFiles);

              const serializedFiles = (files || []).map((f) => ({
                id: f.id,
                name: f.name,
                type: f.type,
                section: "stop",
                sectionIndex: currentLocationIndex,
                timestamp: new Date().toISOString(),
              }));

              // update stops in booking with files
              const updatedStops = (stops || []).map((s, i) =>
                i === currentLocationIndex ? { ...s, files: serializedFiles } : s
              );
              handleUpdateBookingObject("stops", updatedStops, "files_live");

              // merge into bookingFiles removing old stop entries for this index
              // const mergedOther = (bookingFiles || []).filter(
              //   (bf) => !(bf.section === "stop" && bf.sectionIndex === currentLocationIndex)
              // );
              // handleUpdateBookingObject("bookingFiles", [...mergedOther, ...serializedFiles], "files_live");
            }
          } catch (e) {
            Sentry.captureException(e);
            console.error(e);
          }
        }}
        onSave={(instructions, files) => {
          handleSaveInstructions(
            instructions,
            files,
            currentLocationType,
            currentLocationIndex
          );
        }}
      />
    </div>
  );
};

export default ContactInfoScreen;
